// The sidebar's Scores block (GRO-381): Fit and Engagement now, the bucket, and a
// small inline-SVG line graph of both over the last 90 days. It shows nothing at all until the
// account has a score, so on production (scores hidden, table empty) the Company tab is unchanged.

import { useQuery } from '@tanstack/react-query';
import type { AccountScore, BookAccount } from '../api/types';
import { fetchScoreHistory, fetchScores } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { HoverCard } from '../components/HoverCard';
import { ScoreCardBody } from '../book/ScoreCardBody';
import { bucketFromScore, formatScore, levelLabel, missingDataCategories } from '../book/scores';
import { GRAPH_HEIGHT, GRAPH_WIDTH, graphGeometry } from './scoreGraph';
import { PanelSection } from './panelParts';
import { CompetitorToggle } from './CompetitorToggle';

const HISTORY_DAYS = 90;

// The number with the same breakdown hover the board's cells have (GRO-411).
function ScoreFigure({ accountId, score, kind }: { accountId: string; score: AccountScore; kind: 'fit' | 'engagement' }) {
  const label = kind === 'fit' ? 'Fit' : 'Engagement';
  const value = kind === 'fit' ? score.fit_score : score.engagement_score;
  const level = kind === 'fit' ? score.fit_level : score.engagement_level;
  return (
    <div className="scorefigure">
      <span className="scorefiglabel">{label}</span>
      <HoverCard card={() => <ScoreCardBody accountId={accountId} score={score} kind={kind} />} label={`${label} ${formatScore(value)}: how it was reached`}>
        <span className={'scorenum big ' + (level ?? '')}>{formatScore(value)}</span>
      </HoverCard>
      {level ? <span className="scorefiglevel">{levelLabel(level)}</span> : null}
    </div>
  );
}

function ScoreGraph({ accountId }: { accountId: string }) {
  const { getToken } = useAuthContext();
  const historyQuery = useQuery({
    queryKey: ['score-history', accountId, HISTORY_DAYS],
    queryFn: () => fetchScoreHistory(getToken, accountId, HISTORY_DAYS),
    retry: false,
  });
  if (historyQuery.isPending) return <p className="panelempty">Loading history…</p>;
  if (historyQuery.isError) return <p className="panelempty">History could not be loaded.</p>;
  const geometry = graphGeometry(historyQuery.data.points);
  if (geometry.tooFew) return <p className="panelempty">Not enough history for a graph yet. It needs two scored days.</p>;
  return (
    <figure className="scoregraph">
      <svg viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`} role="img"
        aria-label={`Fit and Engagement from ${geometry.firstDay} to ${geometry.lastDay}`}>
        {[0, 50, 100].map(level => {
          const y = 6 + (1 - level / 100) * (GRAPH_HEIGHT - 12);
          return <line key={level} className="scoregrid" x1="0" x2={GRAPH_WIDTH} y1={y} y2={y} />;
        })}
        {geometry.lines.map(line => (
          <g key={line.key} className={'scoreline ' + line.key}>
            {line.path ? <path d={line.path} fill="none" /> : null}
            {line.lastX != null && line.lastY != null ? <circle cx={line.lastX} cy={line.lastY} r="2.5" /> : null}
          </g>
        ))}
      </svg>
      <figcaption>
        <span><i className="scorekey fit" />Fit</span>
        <span><i className="scorekey engagement" />Engagement</span>
        <span className="scorespan">{geometry.firstDay} to {geometry.lastDay}</span>
      </figcaption>
    </figure>
  );
}

function ScoresBody({ accountId, score }: { accountId: string; score: AccountScore }) {
  const bucket = bucketFromScore(score);
  const missing = [...missingDataCategories(score.fit), ...missingDataCategories(score.engagement)];
  const flagged = score.competitor_flagged === true;
  return (
    <>
      <div className="scorefigures">
        <ScoreFigure accountId={accountId} score={score} kind="fit" />
        <ScoreFigure accountId={accountId} score={score} kind="engagement" />
        <div className="scorebadges">
          {bucket ? <span className={'scorepill ' + bucket.tone}>{bucket.label}</span> : null}
          {flagged ? <span className="scorepill bad" title="Fit is held to low: the competitor flag">Competitor</span> : null}
        </div>
      </div>
      <ScoreGraph accountId={accountId} />
      {missing.length ? <p className="scoremissingnote">Data missing, default used: {missing.join(', ')}.</p> : null}
      {flagged || score.competitor_overridden ? <CompetitorToggle accountId={accountId} flagged={flagged} /> : null}
    </>
  );
}

export function ScoresSection({ row, enabled }: { row: BookAccount; enabled: boolean }) {
  const { getToken } = useAuthContext();
  // The same query key the board uses, so opening the panel is a cache hit.
  const scoresQuery = useQuery({
    queryKey: ['scores'],
    queryFn: () => fetchScores(getToken),
    enabled,
    retry: false,
  });
  const payload = scoresQuery.data;
  const score = payload && payload.visible ? payload.by_account_id[row.id] : undefined;
  if (!score) return null;
  return (
    <PanelSection title="Scores" aside={`scored ${score.scored_on}`}>
      <ScoresBody accountId={row.id} score={score} />
    </PanelSection>
  );
}
